import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { PatientSelectionItem } from "../../Types/VoiceAssistant/VoiceAssistantTypes";
import { getEmrNoFromPatientSelectionItem } from "../../utilis/methods/voiceAssistant/patientSelectionVoice";

type Props = {
  open: boolean;
  patients: PatientSelectionItem[];
  onSelect: (patient: PatientSelectionItem) => void;
  onClose: () => void;
};

function patientLabel(item: PatientSelectionItem): string {
  const name =
    item.fullName ||
    [item.firstName, item.middleName, item.lastName].filter(Boolean).join(" ");
  const trimmed = name?.trim();
  if (trimmed) return trimmed;
  const emr = getEmrNoFromPatientSelectionItem(item);
  if (emr) return emr;
  return "Unknown patient";
}

function patientSecondary(item: PatientSelectionItem): string {
  const emr = getEmrNoFromPatientSelectionItem(item);
  const doctor = item.doctorName ? ` · ${item.doctorName}` : "";
  const encounter =
    item.encounterId ?? item.encounterID
      ? ` · Encounter ${item.encounterId ?? item.encounterID}`
      : "";
  return `${emr || "—"}${doctor}${encounter}`;
}

export default function PatientSelectionDialog({
  open,
  patients,
  onSelect,
  onClose,
}: Props) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
        <Typography variant="h6" fontWeight={700} flex={1}>
          Select patient
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
          Multiple patients matched your voice command. Choose one to open EMR.
        </Typography>
        <List disablePadding>
          {patients.map((patient, index) => (
            <ListItemButton
              key={`${patient.encounterId ?? patient.encounterID ?? index}-${patient.patientMasterId ?? patient.patientId ?? index}`}
              onClick={() => onSelect(patient)}
              divider={index < patients.length - 1}
            >
              <ListItemText
                primary={patientLabel(patient)}
                secondary={patientSecondary(patient)}
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
